import { useState, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { techInsightsApiRef } from '@mercedes-benz/tech-insights-react';
import { parseEntityRef } from '@backstage/catalog-model';

export interface NebulaIntegrationsData {
  BlackDuck?: string[];
  SecHub?: string[];
  SonarQube?: string[];
}

export interface ParsedNebulaIntegration {
  BlackDuck?: {
    projectKey: string;
    source: string;
  };
  SecHub?: {
    projectKey: string;
    source: string;
  };
  SonarQube?: {
    projectKey: string;
    source: string;
  };
}

// Helper to safely extract a value from an integrations array
const parseIntegrationValue = (
  data: any,
  service: string,
  field: string,
): string | undefined => {
  try {
    if (!data || !service || !field) return undefined;
    const arr = data[service];
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    // Some entries may include non-JSON messages (eg. "matched file: ...").
    // Try to parse the first JSON-parseable element.
    for (const item of arr) {
      try {
        const parsed = JSON.parse(item);
        if (parsed && field in parsed) return parsed[field];
      } catch (e) {
        // not JSON, continue
      }
    }
    return undefined;
  } catch (err) {
    if (err) {
      /* ignored */
    }
    return undefined;
  }
};

// Helper to safely extract the detection method from an integrations array
const parseDetectionMethod = (
  data: any,
  service: string,
): string | undefined => {
  try {
    if (!data || !service) return undefined;
    const arr = data[service];
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    // Try to parse the first JSON-parseable element and extract detectionMethod
    for (const item of arr) {
      try {
        const parsed = JSON.parse(item);
        if (parsed && parsed.detectionMethod) return parsed.detectionMethod;
      } catch (e) {
        // not JSON, continue
      }
    }
    return undefined;
  } catch (err) {
    if (err) {
      /* ignored */
    }
    return undefined;
  }
};

export const useNebulaIntegrationsData = (
  repositoryValue: string,
): {
  data: ParsedNebulaIntegration | null;
  loading: boolean;
  error?: Error;
} => {
  const techInsightsApi = useApi(techInsightsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [data, setData] = useState<ParsedNebulaIntegration | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const fetchNebulaIntegrations = async () => {
      if (!repositoryValue) {
        setData(null);
        setLoading(false);
        setError(undefined);
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        // Parse the entity reference to get the repository entity
        const entityRef = parseEntityRef(repositoryValue);

        // Get the repository entity from the catalog to access spec.fullName
        const entity = await catalogApi.getEntityByRef(entityRef);

        if (!entity) {
          setData(null);
          setLoading(false);
          return;
        }

        // Get the fullName from spec.fullName, or fall back to namespace/name
        const spec = entity.spec as any;
        const repoFullName =
          spec?.fullName ||
          `${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;

        if (!repoFullName) {
          setData(null);
          setLoading(false);
          return;
        }

        const response = await techInsightsApi
          .techStack()
          .v1.getStackByDataType(
            repoFullName.split('/')[0],
            repoFullName.split('/')[1],
            'NEBULA_INTEGRATIONS',
          );

        const rawData = response?.data?.data as NebulaIntegrationsData;

        if (!rawData) {
          setData(null);
          setLoading(false);
          return;
        }

        // Parse the data for each integration
        const parsedData: ParsedNebulaIntegration = {};

        // BlackDuck - use projectTenant field
        const blackDuckProjectKey = parseIntegrationValue(
          rawData,
          'BlackDuck',
          'projectTenant',
        );
        const blackDuckSource = parseDetectionMethod(rawData, 'BlackDuck');
        if (blackDuckProjectKey || blackDuckSource) {
          parsedData.BlackDuck = {
            projectKey: blackDuckProjectKey || '',
            source: blackDuckSource || '',
          };
        }

        // SecHub - use project field
        const secHubProjectKey = parseIntegrationValue(
          rawData,
          'SecHub',
          'project',
        );
        const secHubSource = parseDetectionMethod(rawData, 'SecHub');
        if (secHubProjectKey || secHubSource) {
          parsedData.SecHub = {
            projectKey: secHubProjectKey || '',
            source: secHubSource || '',
          };
        }

        // SonarQube - use projectKey or projectName field
        const sonarQubeProjectKey =
          parseIntegrationValue(rawData, 'SonarQube', 'projectKey') ||
          parseIntegrationValue(rawData, 'SonarQube', 'projectName');
        const sonarQubeSource = parseDetectionMethod(rawData, 'SonarQube');
        if (sonarQubeProjectKey || sonarQubeSource) {
          parsedData.SonarQube = {
            projectKey: sonarQubeProjectKey || '',
            source: sonarQubeSource || '',
          };
        }

        setData(parsedData);
      } catch (err) {
        setError(err as Error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNebulaIntegrations();
  }, [repositoryValue, techInsightsApi, catalogApi]);

  return { data, loading, error };
};
